import { Card, CardContent } from '../components/ui/card';
import { aboutContent } from '../data/mock';
import { Award, Users, Lightbulb, ShieldCheck, Target, TrendingUp } from 'lucide-react';

const iconMap = {
  award: Award,
  users: Users,
  lightbulb: Lightbulb,
  'shield-check': ShieldCheck
};

const About = () => {
  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Sub-Zero Coolers, LLC
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {aboutContent.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutContent.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-black mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-black/80 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <Target className="text-orange-500" size={20} />
                <span className="text-orange-500 text-sm font-semibold">Our Vision</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Our Vision & Mission
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                {aboutContent.vision}
              </p>
              <p className="text-gray-400 leading-relaxed">
                {aboutContent.foundingStory}
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1730225241545-0fc6f204f7ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwa2l0Y2hlbiUyMHJlZnJpZ2VyYXRvciUyMGNvbW1lcmNpYWx8ZW58MHx8fHwxNzc2Mjg3MDM1fDA&ixlib=rb-4.1.0&q=85"
                alt="SubZero Vision"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute inset-0 bg-orange-500/10 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-400 text-lg">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              return (
                <Card key={index} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-all duration-300 group">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 mb-4 group-hover:bg-orange-500/20 transition-colors">
                      {IconComponent && <IconComponent className="text-orange-500" size={32} />}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                    <p className="text-gray-400 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Journey
            </h2>
            <p className="text-gray-400 text-lg">
              Building excellence one installation at a time
            </p>
          </div>

          <div className="space-y-12">
            {[
              {
                year: "2003",
                title: "20 Years Experience Begins",
                description: "Luis Granda begins his journey in the refrigeration industry, building expertise and reputation."
              },
              {
                year: "2023",
                title: "Company Founded",
                description: "Sub-Zero Coolers, LLC was established, bringing 20 years of experience to Florida's commercial refrigeration market."
              },
              {
                year: "2023-2024",
                title: "Rapid Growth",
                description: "Completed numerous walk-in cooler builds and case settings across Florida, building a strong portfolio."
              },
              {
                year: "2024",
                title: "Service Expansion",
                description: "Expanded services to include full refrigeration installations and supermarket refrigeration systems."
              },
              {
                year: "2025",
                title: "Continued Excellence",
                description: "Fully licensed and insured, continuing to grow through quality workmanship and customer referrals."
              }
            ].map((milestone, index) => (
              <div key={index} className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-orange-500/10 border-2 border-orange-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-orange-500">{milestone.year}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3">{milestone.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Partner With Us?
            </h2>
            <p className="text-gray-400 text-lg">
              Experience the SubZero difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Industry Expertise",
                description: "Over 25 years of experience in commercial refrigeration with certified technicians and engineers."
              },
              {
                icon: TrendingUp,
                title: "Proven Results",
                description: "98% customer satisfaction rate with thousands of successful installations across the nation."
              },
              {
                icon: ShieldCheck,
                title: "Reliable Support",
                description: "24/7 emergency service with rapid response times to keep your business running smoothly."
              }
            ].map((reason, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/10 mb-6">
                    <reason.icon className="text-orange-500" size={40} />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-4">{reason.title}</h3>
                  <p className="text-gray-400">{reason.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
